import { Button } from "@roberttamas/ui";
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { ChevronLeft } from "@/components/icons";
import { STEP_BY_ID, type SetupStepId } from "@/components/setup/steps";

/**
 * Chrome switch for setup steps. The wizard renders steps bare: heading
 * inside the card, Back + primary button in a bottom footer bar. The
 * standalone edit pages (/my-orders/:orderId/<section>) own the title
 * and Back link via PageHeader and want the primary button top-right —
 * they wrap the step in StepChromeProvider with the header's actions
 * element. No provider → wizard rendering, unchanged.
 */
const StepChromeContext = createContext<HTMLElement | null>(null);

export function StepChromeProvider({
  actionsEl,
  children,
}: {
  actionsEl: HTMLElement;
  children: ReactNode;
}) {
  return (
    <StepChromeContext.Provider value={actionsEl}>
      {children}
    </StepChromeContext.Provider>
  );
}

/** True on a standalone edit page — the card hides its own heading. */
export function useStandaloneStep(): boolean {
  return useContext(StepChromeContext) !== null;
}

/**
 * A step card's heading: the canonical step name (identical to its
 * stepper label) plus the guided subtitle. Renders nothing standalone —
 * there the PageHeader owns the title.
 */
export function StepHeading({ step }: { step: SetupStepId }) {
  const { t } = useTranslation();
  const standalone = useStandaloneStep();
  if (standalone) return null;
  const meta = STEP_BY_ID[step];
  return (
    <div>
      <h1 className="text-2xl font-semibold">{t(meta.labelKey)}</h1>
      <p className="text-sm text-muted-foreground">{t(meta.subtitleKey)}</p>
    </div>
  );
}

/**
 * A step's footer. Wizard: the classic bottom bar (ghost Back or a
 * spacer, then the primary button). Standalone: the bar disappears and
 * the primary button is portaled into the PageHeader's actions slot;
 * onBack is ignored — the header's Back link covers it.
 */
export function StepFooter({
  onBack,
  children,
}: {
  onBack: (() => void) | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const actionsEl = useContext(StepChromeContext);
  if (actionsEl) return createPortal(children, actionsEl);
  return (
    <div className="flex items-center justify-between border-t pt-4">
      {onBack ? (
        <Button type="button" variant="ghost" onClick={onBack}>
          <ChevronLeft className="h-4 w-4" /> {t("common.back")}
        </Button>
      ) : (
        <span />
      )}
      {children}
    </div>
  );
}
